import apiClient from './api';

export interface AdAccount {
    _id: string;
    accountId: string;
    name: string;
    currency: string;
    timezone: string;
    isActive: boolean;
    amountSpent: number;
    lastSyncedAt: string;
}

export interface Campaign {
    _id: string;
    campaignId: string;
    name: string;
    status: string;
    objective: string;
    dailyBudget: number;
    lifetimeBudget: number;
    adAccountId: AdAccount;
}

export interface AdSet {
    _id: string;
    adSetId: string;
    name: string;
    status: string;
    dailyBudget: number;
    lifetimeBudget: number;
    campaignId: Campaign;
    adAccountId: AdAccount;
}

export interface Ad {
    _id: string;
    adId: string;
    name: string;
    status: string;
    creativeId: string;
    adSetId: AdSet;
    campaignId: Campaign;
    adAccountId: AdAccount;
}

export interface AdInsight {
    _id: string;
    date: string;
    level: string;
    spend: number;
    results: number;
    costPerResult: number;
    impressions: number;
    clicks: number;
    ctr: number;
    cpc: number;
    cpm: number;
    reach: number;
    frequency: number;
    adAccountId: AdAccount;
    campaignId?: Campaign;
    adSetId?: AdSet;
    adId?: Ad;
}

export interface InsightsSummary {
    totalSpend: number;
    totalResults: number;
    totalImpressions: number;
    totalClicks: number;
    totalReach: number;
    avgCostPerResult: number;
}

export interface FilterParams {
    adAccountId?: string;
    campaignId?: string;
    adSetId?: string;
    startDate?: string;
    endDate?: string;
    period?: string;
    status?: string;
    page?: number;
    limit?: number;
}

export const adsService = {
    // Ad Accounts
    getAdAccounts: async (): Promise<AdAccount[]> => {
        const response = await apiClient.get('/ads/accounts');
        return response.data;
    },

    syncAdAccounts: async (): Promise<{ count: number; data: AdAccount[] }> => {
        const response = await apiClient.post('/ads/accounts/sync');
        return response.data;
    },

    // Campaigns
    getCampaigns: async (filter: FilterParams = {}): Promise<{ data: Campaign[]; total: number }> => {
        const response = await apiClient.get('/ads/campaigns', { params: filter });
        return response.data;
    },

    syncCampaigns: async (adAccountId: string): Promise<{ count: number }> => {
        const response = await apiClient.post(`/ads/campaigns/sync/${adAccountId}`);
        return response.data;
    },

    // Ad Sets
    getAdSets: async (filter: FilterParams = {}): Promise<{ data: AdSet[]; total: number }> => {
        const response = await apiClient.get('/ads/adsets', { params: filter });
        return response.data;
    },

    syncAdSets: async (adAccountId: string): Promise<{ count: number }> => {
        const response = await apiClient.post(`/ads/adsets/sync/${adAccountId}`);
        return response.data;
    },

    // Ads
    getAds: async (filter: FilterParams = {}): Promise<{ data: Ad[]; total: number }> => {
        const response = await apiClient.get('/ads', { params: filter });
        return response.data;
    },

    syncAds: async (adAccountId: string): Promise<{ count: number }> => {
        const response = await apiClient.post(`/ads/sync/${adAccountId}`);
        return response.data;
    },

    // Toggle Status
    toggleStatus: async (id: string, level: 'campaign' | 'adset' | 'ad', action: 'ACTIVE' | 'PAUSED'): Promise<{ success: boolean }> => {
        const response = await apiClient.post('/ads/toggle', { id, level, action });
        return response.data;
    },

    // Insights
    getInsights: async (filter: FilterParams = {}): Promise<{ data: AdInsight[]; summary: InsightsSummary }> => {
        const response = await apiClient.get('/ads/insights', { params: filter });
        return response.data;
    },

    syncInsights: async (adAccountId: string, startDate?: string, endDate?: string): Promise<{ count: number }> => {
        const response = await apiClient.post(`/ads/insights/sync/${adAccountId}`, null, {
            params: { startDate, endDate }
        });
        return response.data;
    },

    // Reports
    getWeeklyReport: async (filter: FilterParams = {}): Promise<any[]> => {
        const response = await apiClient.get('/ads/reports/weekly', { params: filter });
        return response.data;
    },

    getMonthlyReport: async (filter: FilterParams = {}): Promise<any[]> => {
        const response = await apiClient.get('/ads/reports/monthly', { params: filter });
        return response.data;
    },

    // Full Sync
    syncAll: async (adAccountId: string, startDate?: string, endDate?: string): Promise<{ campaigns: number; adSets: number; ads: number; insights: number }> => {
        const response = await apiClient.post(`/ads/sync-all/${adAccountId}`, null, {
            params: { startDate, endDate }
        });
        return response.data;
    },
};

export default adsService;
